<template>
  <div class="d-flex flex-row" style="height: 100%">
    <!-- map container height is the whole page minus the 64px header -->
    <div style="min-width: 256px; width: 100%; height: calc(100vh - 64px))">
      <Map
        ref="map"
        :zoom-control="true"
        :geocoder="geocoder"
        :geocoderContainerId="geocoderContainerId"
        :focus-on-geocoder="true"
        :draw-tools="
          $route.path === '/monitor' ||
          (!this.$route.query.view &&
            show_draw_tools &&
            !this.$route.params.monitor_id)
        "
        :show-coords="true"
        :show-scale="true"
        :styles-control="true"
        :min-zoom="1"
        :max-zoom="19"
        :polygons="[]"
        :drawPolygons="drawnFeature ? [drawnFeature] : []"
        :bounds="bounds"
        :bounds-options="{ padding: 200 }"
        :layers-control="$route.path === '/explore' ? true : false"
        layers-control-position="top-right"
        map-style="mapbox://styles/mapbox/dark-v10"
        @load="onMapLoad"
        @drawChange="onDrawChange"
        draw-tips-position="bottom-right"
        :show-attribution="false"
      />
    </div>
    <div>
      <explore-metadata
        v-if="$route.path === '/explore'"
        ref="sidebar"
        :map="$refs.map || {}"
        :map_loaded="mapLoaded"
        key="explore"
        @set_drawn_feature="drawnFeature = $event.aoi"
        @set_draw_tools="show_draw_tools = $event.draw"
      />
      <view-select
        v-else-if="this.$route.query.view"
        :select_request="select_request"
        :select_response="select_response"
        :map="$refs.map || {}"
        :map_loaded="mapLoaded"
        key="view"
      />
      <create-select
        ref="sidebar"
        v-else-if="$route.path === '/select'"
        @select_response="receive_response($event.request, $event.response)"
        :map="$refs.map || {}"
        :drawnFeature="drawnFeature"
        :map_loaded="mapLoaded"
        key="select"
      />
      <create-monitor
        ref="sidebar"
        v-else-if="$route.name === 'create-monitor'"
        @select_response="receive_response($event.request, $event.response)"
        :map="$refs.map || {}"
        :drawnFeature="drawnFeature"
        :map_loaded="mapLoaded"
        key="create-monitor"
      />
      <view-monitor
        v-else-if="this.$route.params.monitor_id"
        :map="$refs.map || {}"
        :map_loaded="mapLoaded"
        key="view-monitor"
      />
      <place-order
        ref="sidebar"
        v-else
        :map="$refs.map || {}"
        :map_loaded="mapLoaded"
        key="order"
      />
    </div>
    <div v-if="!mapLoaded" class="map-loading-overlay">
      <spinner class="text-caption" small>Initializing map...</spinner>
    </div>
  </div>
</template>

<script>
import CreateSelect from "@/components/select/CreateSelect.vue";
import PlaceOrder from "@/components/order/PlaceOrder.vue";
import ViewSelect from "@/components/select/ViewSelect.vue";
import ExploreMetadata from "@/components/explore/ExploreMetadata.vue";
import ViewMonitor from "@/components/monitor/ViewMonitor.vue";
import CreateMonitor from "@/components/monitor/CreateMonitor.vue";
import Map from "@/components/map/index";
import Spinner from "@/components/shared/Spinner";
import mapUtils from "@/utils/map";
import monitorUtils from "@/utils/monitor";
import turfBbox from "@turf/bbox";
import turfBboxPoly from "@turf/bbox-polygon";

export default {
  components: {
    CreateSelect,
    PlaceOrder,
    ViewSelect,
    Map,
    Spinner,
    ExploreMetadata,
    ViewMonitor,
    CreateMonitor,
  },
  mixins: [mapUtils],
  data() {
    return {
      select_request: null,
      select_response: null,
      mapLoaded: false,
      drawnFeature: null,
      bounds: [
        [-180, 90],
        [180, -90],
      ],
      geocoder: null,
      geocoderContainerId: null,
      show_draw_tools: true,
    };
  },
  watch: {
    $route(to, from) {
      // console.log(`$route(to=${to.fullPath}, from=${from.fullPath})`);
      // clear drawn features and restore draw tools
      this.show_draw_tools = true;
      this.drawnFeature = null;

      // always set geocoder to false so it is reloaded with the new sidebar needs a geocoder between routes
      this.geocoder = false;

      // always clear the draw layer and polygons between routes
      this.$refs.map.clear();

      // give a little time for the sidebar to render
      this.$nextTick(() => {
        this.onMapLoad();
      });
    },
  },
  beforeRouteEnter(to, from, next) {
    // if we are viewing a monitor make sure we have the right permissions
    if (to.path.startsWith("/monitor")) {
      monitorUtils.checkMonitorsEnabledPermissions(to, from, next);
    } else {
      next();
    }
  },
  mounted() {
    this.geocoder = false;
  },
  methods: {
    receive_response(request, response) {
      this.select_request = request;
      this.select_response = response;
    },
    onMapLoad() {
      // console.log(`Select onMapLoad()`);
      this.mapLoaded = true;

      if (!this.$route.query.view && !this.$route.params.monitor_id) {
        // add draw polygons if we have them as well as set the bounds
        this.drawnFeature = this.getDrawnFeature();
        this.bounds = this.getBounds();
        this.geocoderContainerId = "geocoder-container";
        this.geocoder = true;
      } else {
        this.drawnFeature = null;
        this.bounds = null;
        this.geocoderContainerId = null;
        this.geocoder = false;
      }
    },
    onDrawChange(featureCollection) {
      if (this.$refs.sidebar && this.$refs.sidebar.setFeatureCollection) {
        this.$refs.sidebar.setFeatureCollection(featureCollection);
      }
    },
    getDrawnFeature() {
      // explore to select AOI
      if (this.$route.params?.geometry) {
        return this.$route.params;
      } // edit select AOI (intersects)
      else if (this.$route.params?.request_details?.intersects) {
        return this.parseGeom(this.$route.params?.request_details?.intersects);
      } // edit select AOI (bbox)
      else if (this.$route.params?.request_details?.bbox) {
        return turfBboxPoly(this.$route.params?.request_details?.bbox);
      } else {
        return null;
      }
    },
    getBounds() {
      if (!this.$route.query.view) {
        if (this.drawnFeature) {
          return turfBbox(this.drawnFeature);
        } else {
          return null;
        }
      } else {
        if (this.$route.params?.request_details?.intersects) {
          return turfBbox({
            type: "Feature",
            geometry: this.parseGeom(
              this.$route.params.request_details.intersects
            ),
          });
        } else {
          return null;
        }
      }
    },
  },
};
</script>

<style lang="scss">
.map-sidebar-overlay {
  position: absolute;
  left: 10px;
  top: 10px;
  background-color: transparent;
  z-index: 3;
  min-width: 350px;
  width: 33%;
  max-width: 500px;
}

.map-sidebar {
  background-color: rgba(0, 0, 0, 0.5);
}

.map-search-container {
  position: absolute;
  top: 10px;
}

@media (max-width: 1050px) {
  .map-search-container {
    left: 360px;
  }
}

@media (min-width: 1050px) and (max-width: 1500px) {
  .map-search-container {
    left: calc(33% + 10px);
  }
}

@media (min-width: 1500px) {
  .map-search-container {
    left: 510px;
  }
}

.map-loading-overlay {
  top: 0;
  bottom: 0;
  margin: auto;
  position: absolute;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 4;
}

.map-padded-overlay {
  top: 35%;
  bottom: 35%;
  margin: auto;
  position: absolute;
  left: 35%;
  right: 35%;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 3;
}

.legend-swatch {
  border-style: solid;
  border-color: rgba(204, 204, 204, 0.8);
  border-width: 2px;
  height: 15px;
  width: 15px;
}

.map-legend-container {
  background-color: rgba(0, 0, 0, 0.5);
}
</style>
